import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { TrendingUp, TrendingDown, Coffee, Users, Receipt, DollarSign, Globe, MapPin } from "lucide-react";
import { superApi } from "../lib/admin";

/**
 * Founder-facing growth panel: country breakdown, MoM deltas, 30-day
 * sparklines for cafés, customers, orders, revenue. Pulls a single payload
 * from GET /super/growth — no chart library dependency, just inline SVG.
 */
const money = (cents) => `$${((cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const GrowthAnalytics = () => {
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");

    useEffect(() => {
        superApi.get("/super/growth")
            .then((r) => setData(r.data))
            .catch((e) => setErr(e?.response?.data?.detail || "Failed to load growth"));
    }, []);

    if (err) return (
        <section className="mt-14" data-testid="growth-analytics">
            <div className="rounded-3xl bg-white border border-[#E8DCCB] p-6 text-sm text-[#B4361E]">
                {err}
            </div>
        </section>
    );
    if (!data) return null;

    return (
        <section className="mt-14" data-testid="growth-analytics">
            <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#FF4F18]">
                Growth · last 30 days
            </div>
            <h2 className="mt-2 font-display font-black text-3xl">
                Where the business is going.
            </h2>
            <p className="mt-1 text-sm text-[#8C7A72]">
                One glance — country split, month-over-month, and the last 30-day shape of the platform.
            </p>

            {/* Row 1 — Country breakdown pills */}
            <div className="mt-6 grid gap-3 md:grid-cols-2">
                <CountryCard
                    label="Cafés by country"
                    icon={Coffee}
                    total={data.totals.cafes_total}
                    au={data.countries.cafes.AU}
                    other={data.countries.cafes.Other}
                    testid="country-cafes"
                />
                <CountryCard
                    label="Customers by country"
                    icon={Users}
                    total={data.totals.customers_total}
                    au={data.countries.customers.AU}
                    other={data.countries.customers.Other}
                    testid="country-customers"
                />
            </div>

            {/* Row 2 — Growth deltas + sparklines */}
            <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                <SparklineCard
                    label="New cafés"
                    icon={Coffee}
                    delta={data.deltas_30d.cafes}
                    series={data.series_30d.cafes_added}
                    format={(v) => v.toLocaleString()}
                    testid="spark-cafes"
                />
                <SparklineCard
                    label="New customers"
                    icon={Users}
                    delta={data.deltas_30d.customers}
                    series={data.series_30d.customers_added}
                    format={(v) => v.toLocaleString()}
                    testid="spark-customers"
                />
                <SparklineCard
                    label="Orders"
                    icon={Receipt}
                    delta={data.deltas_30d.orders}
                    series={data.series_30d.orders}
                    format={(v) => v.toLocaleString()}
                    testid="spark-orders"
                />
                <SparklineCard
                    label="Fee revenue"
                    icon={DollarSign}
                    delta={data.deltas_30d.revenue_cents}
                    series={data.series_30d.revenue_cents}
                    format={money}
                    testid="spark-revenue"
                    accent
                />
            </div>
        </section>
    );
};

const CountryCard = ({ label, icon: Icon, total, au, other, testid }) => {
    const auPct = total ? Math.round((au / total) * 100) : 0;
    return (
        <div className="rounded-3xl bg-white border border-[#E8DCCB] p-5" data-testid={testid}>
            <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.25em] text-[#8C7A72] font-semibold">
                <Icon className="h-3.5 w-3.5" strokeWidth={2.5} />
                {label}
            </div>
            <div className="mt-2 font-display font-black text-3xl">{total.toLocaleString()}</div>
            <div className="mt-3 h-3 rounded-full bg-[#F9F6F0] overflow-hidden flex">
                <div
                    className="h-full bg-[#FF4F18] transition-all"
                    style={{ width: `${auPct}%` }}
                    data-testid={`${testid}-au-bar`}
                />
                <div
                    className="h-full bg-[#2C1A12] transition-all"
                    style={{ width: `${100 - auPct}%` }}
                />
            </div>
            <div className="mt-2 flex items-center gap-4 text-xs">
                <div className="flex items-center gap-1.5">
                    <MapPin className="h-3 w-3 text-[#FF4F18]" strokeWidth={2.5} />
                    <span className="font-display font-black">AU {au.toLocaleString()}</span>
                    <span className="text-[#8C7A72]">· {auPct}%</span>
                </div>
                <div className="flex items-center gap-1.5">
                    <Globe className="h-3 w-3 text-[#2C1A12]" strokeWidth={2.5} />
                    <span className="font-display font-black">Other {other.toLocaleString()}</span>
                    <span className="text-[#8C7A72]">· {100 - auPct}%</span>
                </div>
            </div>
        </div>
    );
};

const SparklineCard = ({ label, icon: Icon, delta, series, format, accent, testid }) => {
    const now = delta?.current ?? 0;
    const pct = delta?.pct; // null if previous was zero (no denominator)
    const up = pct === null ? true : pct >= 0;
    const bgClasses = accent
        ? "bg-[#2C1A12] text-white border-[#2C1A12]"
        : "bg-white border-[#E8DCCB]";
    const subClass = accent ? "text-white/50" : "text-[#8C7A72]";
    return (
        <div className={`rounded-3xl p-5 border ${bgClasses}`} data-testid={testid}>
            <div className={`flex items-center justify-between text-[10px] uppercase tracking-[0.25em] font-semibold ${subClass}`}>
                <div className="flex items-center gap-2">
                    <Icon className="h-3.5 w-3.5" strokeWidth={2.5} />
                    {label}
                </div>
                {pct !== null && (
                    <div
                        className={`flex items-center gap-1 font-display font-black text-xs ${
                            up ? "text-[#7DE0A5]" : "text-[#FF8A6E]"
                        }`}
                        data-testid={`${testid}-delta`}
                    >
                        {up ? <TrendingUp className="h-3 w-3" strokeWidth={3} /> : <TrendingDown className="h-3 w-3" strokeWidth={3} />}
                        {up ? "+" : ""}{pct}%
                    </div>
                )}
                {pct === null && delta?.current > 0 && (
                    <div className="text-[10px] font-bold text-[#FF4F18]">
                        NEW
                    </div>
                )}
            </div>
            <div className="mt-2 font-display font-black text-3xl">
                {format(now)}
            </div>
            <div className={`text-xs ${subClass}`}>
                {pct === null ? "no comparable prior period" : `vs ${format(delta.previous)} prior 30 days`}
            </div>
            <Sparkline data={series} accent={accent} />
        </div>
    );
};

/** Pure SVG sparkline. `data` is an array of numbers; scales to card width. */
const Sparkline = ({ data, accent }) => {
    if (!data || data.length === 0) return null;
    const w = 240;
    const h = 40;
    const max = Math.max(...data, 1);
    const stepX = w / Math.max(1, data.length - 1);
    const pts = data.map((v, i) => [
        i * stepX,
        h - (v / max) * (h - 4) - 2,
    ]);
    const line = pts.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
    // Area path (line + close to bottom for gradient fill)
    const area = `${line} L${w},${h} L0,${h} Z`;
    const stroke = accent ? "#FFD166" : "#FF4F18";
    const fill = accent ? "url(#gradAccent)" : "url(#gradLight)";
    return (
        <svg
            viewBox={`0 0 ${w} ${h}`}
            preserveAspectRatio="none"
            className="mt-3 w-full h-10"
            aria-hidden
        >
            <defs>
                <linearGradient id="gradLight" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#FF4F18" stopOpacity="0.35" />
                    <stop offset="100%" stopColor="#FF4F18" stopOpacity="0" />
                </linearGradient>
                <linearGradient id="gradAccent" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#FFD166" stopOpacity="0.4" />
                    <stop offset="100%" stopColor="#FFD166" stopOpacity="0" />
                </linearGradient>
            </defs>
            <path d={area} fill={fill} />
            <path d={line} fill="none" stroke={stroke} strokeWidth="1.75" strokeLinejoin="round" strokeLinecap="round" />
        </svg>
    );
};

export default GrowthAnalytics;
